<template>
  <div data-testid="overview-tab">
    <TextWithImageGridSection :images="intro.images" :expand-images="false">
      <div
        class="flex w-full flex-col gap-y-3 pb-4 xl:pb-0"
        data-testid="banner-descriptions"
      >
        <h3 data-testid="banner-title" class="text-xl font-bold">
          {{ intro.title }}
        </h3>

        <div class="flex w-full flex-col gap-y-3 text-base">
          <p v-for="(paragraph, index) in intro.body" :key="index">
            {{ paragraph }}
          </p>
        </div>

        <ButtonContactUs
          :services="initialFields"
          text="Contact Us"
          class="mt-1 px-[31.07px] py-[21px] text-sm"
        />
      </div>
    </TextWithImageGridSection>
  </div>
</template>

<script setup lang="ts">
import type { TMarketServiceConfig } from '~/src/types/markets'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import TextWithImageGridSection from '~/components/shared/common/TextWithImageGridSection.vue'

const intro = {
  title: 'Battery Emissions Analyser',
  body: [
    'Valuable insights into the carbon emissions of the key lithium-ion battery chemistries today. With global lithium-ion battery production set to grow significantly, understanding the environmental impact of these technologies is crucial.',
    'Benchmark’s Battery Emissions Analyser (BEA) offers essential emission data using Life Cycle Assessment (LCA) inventories, alongside Benchmark’s exclusive cost models, to identify environmental hotspots throughout various stages of cell production, aiding supply chain decisions.',
    'The choice of raw material sourcing, production location and battery manufacturing itself significantly affects carbon competitiveness. The energy source used during both cathode active material production and anode production also influences carbon intensity in various battery chemistries compared with another.',
    'These types of assessments, scenario testing and comparisons are all available on the BEA tool, at your fingertips. This information is vital for stakeholders in the energy storage and battery industries, supporting environmentally conscious supply chain decisions in a growing market.',
  ],
  images: {
    one: {
      url: 'https://res.cloudinary.com/benchmarkminerals/image/upload/f_auto,q_auto,w_400/v1749755749/assets/esg/lithium-ion-batteries/Benchmark_Minerals_lithium_mine_flat_shades_of_yellow_and_white_1bd378f6-3e1f-4610-8990-343340814004_2_bxjwny.jpg',
      alt: 'Lithium ion Batteries ESG',
    },
    two: {
      url: 'https://res.cloudinary.com/benchmarkminerals/image/upload/f_auto,q_auto,w_400/v1749755749/assets/esg/lithium-ion-batteries/Mask_group_yow8of.jpg',
      alt: 'Lithium ion Batteries ESG',
    },
    three: {
      url: 'https://res.cloudinary.com/benchmarkminerals/image/upload/f_auto,q_auto,w_400,dpr_auto/v1749755748/assets/esg/lithium-ion-batteries/Mask_group-1_eqmvqv.jpg',
      alt: 'Lithium ion Batteries ESG',
    },
  },
}

definePageMeta({
  isTab: true,
  scrollToTop: false,
  layout: 'services',
  middleware: () => {
    const { isAuthenticated } = useAuthentication()

    if (!isAuthenticated()) return

    return navigateTo(
      '/battery-gigafactory/sustainability/battery-emissions-analyser',
      {
        replace: true,
      },
    )
  },
})

defineProps<{
  market: TMarketServiceConfig
  initialFields: string[]
}>()
</script>
